(() => {
  const key = Symbol.for('trh.home-motion');
  if (document[key]) { document[key].refresh(); return; }

  class HomeMotion {
    constructor() {
      this.items = new Map();
      this.seen = new WeakSet();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.motion.addEventListener('change', () => this.refresh(), options);
      document.addEventListener('DOMContentLoaded', () => this.refresh(), options);
      document.addEventListener('shopify:section:load', () => this.refresh(), options);
      document.addEventListener('shopify:section:reorder', () => this.refresh(), options);
      document.addEventListener('shopify:section:unload', event => this.remove(event.target), options);
      for (const event of ['shopify:section:select', 'shopify:block:select']) {
        document.addEventListener(event, e => this.showWithin(e.target.closest?.('.shopify-section') || e.target), options);
      }
      document.addEventListener('focusin', event => this.showWithin(event.target), options);
      document.addEventListener('click', event => this.anchorClick(event), options);
      document.addEventListener('animationend', event => {
        if (event.animationName === 'trh-home-reveal') this.finish(event.target);
      }, options);
      window.addEventListener('hashchange', () => this.showAnchor(location.hash), options);
      window.addEventListener('pagehide', () => this.stop(true), options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      this.refresh();
    }

    refresh() {
      const main = document.querySelector('main[data-template="index"]');
      const hero = main?.querySelector('trh-hero-carousel');
      if (!main || hero?.dataset.homeMotion !== 'true' || this.motion.matches || !window.IntersectionObserver) {
        this.stop();
        return;
      }
      if (this.main && this.main !== main) this.stop();
      this.main = main;
      try {
        if (!this.observer) this.observer = new IntersectionObserver(entries => {
          try { this.enter(entries); } catch { this.stop(); }
        }, { rootMargin: '0px 0px -48px 0px', threshold: 0.01 });
        for (const element of this.items.keys()) {
          if (!element.isConnected || !main.contains(element)) this.forget(element);
        }
        const groups = [
          ['.trh-task-categories .trh-section-heading, .trh-featured .trh-section-heading, .trh-guide .trh-section-heading, .trh-faq > div:first-child', 'up'],
          ['.trh-categories > .trh-category-motion, .trh-product-grid > div', 'up'],
          ['.trh-scene-story__copy, .trh-guide-item, .trh-guide-help, .trh-home-brand__inner', 'up'],
          ['.trh-partners-static__grid, .trh-scene-story__figure, .trh-scene-story--packing > .trh-scene-story__picture', 'fade'],
        ];
        for (const [selector, kind] of groups) {
          for (const element of main.querySelectorAll(selector)) {
            if (this.seen.has(element) || this.items.has(element)) continue;
            const rect = element.getBoundingClientRect();
            if (!rect.width || !rect.height) continue;
            // Never hide content the visitor can already see, including restored scroll positions.
            if (rect.top < window.innerHeight) { this.seen.add(element); continue; }
            element.dataset.homeReveal = kind;
            element.dataset.homeRevealState = 'pending';
            this.items.set(element, kind);
            this.observer.observe(element);
          }
        }
        main.dataset.trhHomeMotion = 'on';
        this.showAnchor(location.hash);
        if (document.activeElement) this.showWithin(document.activeElement);
      } catch { this.stop(); }
    }

    enter(entries) {
      const rows = new Map();
      const step = window.innerWidth < 700 ? 50 : 80;
      const arriving = entries.filter(entry => entry.isIntersecting && this.items.has(entry.target))
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);
      for (const entry of arriving) {
        const element = entry.target;
        const group = element.closest('.trh-categories, .trh-product-grid, .trh-guide-grid');
        let order = 0;
        if (group) {
          if (!rows.has(group)) rows.set(group, new Map());
          const row = group.classList.contains('trh-guide-grid') ? 0 : Math.round(entry.boundingClientRect.top / 8);
          const groupRows = rows.get(group);
          order = groupRows.get(row) || 0;
          groupRows.set(row, order + 1);
        }
        element.style.setProperty('--trh-motion-delay', `${Math.min(order, 3) * step}ms`);
        element.dataset.homeRevealState = 'revealing';
        this.seen.add(element);
        this.observer.unobserve(element);
      }
    }

    finish(element) {
      if (!this.items.has(element)) return;
      this.observer?.unobserve(element);
      this.seen.add(element);
      element.dataset.homeRevealState = 'shown';
      element.style.removeProperty('--trh-motion-delay');
      this.items.delete(element);
    }

    showWithin(target) {
      if (!target || target === document.body || target === this.main || target === document.documentElement) return;
      for (const element of this.items.keys()) {
        if (target.contains(element) || element.contains(target)) this.finish(element);
      }
    }

    showAnchor(hash) {
      if (!hash || hash === '#') return;
      try {
        const target = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (target === this.main) {
          for (const element of this.items.keys()) this.finish(element);
        } else this.showWithin(target);
      } catch { /* A malformed fragment must not affect the page. */ }
    }

    anchorClick(event) {
      const link = event.target.closest?.('a[href]');
      if (!link) return;
      try {
        const url = new URL(link.href, location.href);
        if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search) this.showAnchor(url.hash);
      } catch { /* Leave unsupported links to the browser. */ }
    }

    forget(element) {
      this.observer?.unobserve(element);
      delete element.dataset.homeReveal;
      delete element.dataset.homeRevealState;
      element.style.removeProperty('--trh-motion-delay');
      this.items.delete(element);
    }

    remove(section) {
      for (const element of this.items.keys()) {
        if (section.contains(element)) this.forget(element);
      }
      const hero = this.main?.querySelector('trh-hero-carousel');
      if (hero && section.contains(hero)) this.stop();
    }

    stop(remember = false) {
      this.observer?.disconnect();
      this.observer = null;
      for (const element of this.items.keys()) {
        if (remember || element.dataset.homeRevealState === 'revealing') this.seen.add(element);
        this.forget(element);
      }
      if (this.main) delete this.main.dataset.trhHomeMotion;
    }
  }

  document[key] = new HomeMotion();
})();
